import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config/api';
import { setSessionExpiredHandler } from '../utils/apiClient';

// ============================================
// AUTH CONTEXT (Updated with Admin)
// ============================================
export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState(null);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState(null);
  const [needsConsent, setNeedsConsent] = useState(false);

  // Fetch CSRF token
  const fetchCsrfToken = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/get-csrf-token.php`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.csrf_token) {
        setCsrfToken(data.csrf_token);
      }
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
    }
  }, []);

  // Load user's permissions
  const loadUserPermissions = useCallback(async () => {
    if (!user || !tenant) {
      setPermissions([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/my-permissions.php`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setPermissions(data.permissions || []);
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
      setPermissions([]);
    }
  }, [user, tenant]);

  // Check if user has a permission
  const hasPermission = useCallback((permissionKey) => {
    // Superadmin always has all permissions
    if (user?.is_superadmin === 1 || isSuperAdmin) {
      return true;
    }
    // Admin/Owner also has all permissions (tenant-scoped)
    if (isAdmin) {
      return true;
    }
    return permissions.includes(permissionKey);
  }, [user, isSuperAdmin, isAdmin, permissions]);

  // Check if user has given all required consents
  const checkConsents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/consents/check.php`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        // If any required consents are missing or outdated, show consent modal
        const needsToConsent = !data.all_required_granted;
        setNeedsConsent(needsToConsent);
        return !needsToConsent; // Return true if all consents are given
      }
      return true; // If check fails, let them through (fail open)
    } catch (error) {
      console.error('Consent check error:', error);
      return true; // Fail open
    }
  }, []);

  // Called when user completes consent flow
  const onConsentComplete = useCallback(() => {
    setNeedsConsent(false);
  }, []);

  // Load permissions when user or tenant changes
  useEffect(() => {
    if (user && tenant) {
      loadUserPermissions();
    } else {
      setPermissions([]);
    }
  }, [user, tenant, loadUserPermissions]);

  // Fetch CSRF token on mount
  useEffect(() => {
    fetchCsrfToken();
  }, [fetchCsrfToken]);

  // Set up global session expired handler
  useEffect(() => {
    setSessionExpiredHandler((message) => {
      console.log('📢 Session expired handler called:', message);
      setSessionExpiredMessage(message);
      // Clear user data
      setUser(null);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setTenant(null);
      setTenants([]);
      setPermissions([]);
    });
  }, []);

  // Registration token detection
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const registrationToken = urlParams.get('token');

    if (registrationToken) {
      console.log('🎫 Registration token detected, skipping auth check');
      setLoading(false);
      return;
    }

    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const r = await fetch(`${API_URL}/check-auth.php`, { credentials: 'include' });

      // Check if response is OK
      if (!r.ok) {
        // Session expired or auth failed
        setUser(null);
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setTenant(null);
        setTenants([]);
        setPermissions([]);
        setLoading(false);
        return;
      }

      const d = await r.json();

      if (d.authenticated) {
        setUser(d.user);
        setIsSuperAdmin(d.user.is_superadmin === 1);

        // Backend already auto-selects first tenant if none is selected
        const currentTenant = d.tenants?.find(t => t.id === d.current_tenant_id) || null;

        setTenant(currentTenant);
        setTenants(d.tenants || []);

        const roleLower = currentTenant?.role?.toLowerCase() || '';
        const isAdminRole = currentTenant && ['owner', 'admin', 'superadmin'].includes(roleLower);
        setIsAdmin(d.user.is_superadmin === 1 || isAdminRole);

        try {
          const ra = await fetch(`${API_URL}/admin/check.php`, { credentials: 'include' });
          const da = await ra.json();
          if (da.success && da.is_admin) {
            setIsAdmin(true);
          }
        } catch {
          // Not admin, that's fine
        }

        // Check consents on page load/refresh
        await checkConsents();
      } else {
        setUser(null);
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setTenant(null);
        setTenants([]);
        setPermissions([]);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setTenant(null);
      setTenants([]);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const r = await fetch(`${API_URL}/login.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type':'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({ email, password, csrf_token: csrfToken })
      });
      const d = await r.json();
      if (d.success) {
        await checkAuth();
        await fetchCsrfToken(); // Refresh token after login

        // Check if user has given all required consents
        await checkConsents();
      }
      return d;
    } catch (error) {
      console.error('Login error:', error);
      // Check if user is offline
      if (!navigator.onLine) {
        return { success: false, message: 'Ei verkkoyhteyttä. Tarkista internet-yhteys ja yritä uudelleen.' };
      }
      return { success: false, message: 'Verkkovirhe. Yritä myöhemmin uudelleen.' };
    }
  };

  const register = async (email, password, firstName, lastName, token) => {
    try {
      const r = await fetch(`${API_URL}/register.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type':'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({ email, password, first_name: firstName, last_name: lastName, token, csrf_token: csrfToken })
      });
      const d = await r.json();
      return d;
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, message: 'Network error' };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/logout.php`, { credentials:'include' });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setTenant(null);
    setTenants([]);
    setPermissions([]);
  };

  const switchTenant = async (tenantId) => {
    try {
      console.log('🔄 Switching to tenant:', tenantId);
      const res = await fetch(`${API_URL}/admin/switch-tenant.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({ tenant_id: tenantId, csrf_token: csrfToken })
      });

      console.log('📡 Response status:', res.status);

      if (!res.ok) {
        const txt = await res.text();
        console.error('❌ Switch failed:', res.status, txt);

        // Session expired - clear user state and show message
        if (res.status === 401 || res.status === 403) {
          setUser(null);
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setTenant(null);
          setTenants([]);
          setPermissions([]);
          setSessionExpiredMessage('Istuntosi on vanhentunut. Kirjaudu uudelleen.');
          return;
        }

        alert(`Organisaation vaihto epäonnistui (${res.status})`);
        return;
      }

      const data = await res.json();
      console.log('✅ Switch response:', data);

      await checkAuth();
      console.log('✅ Tenant switched successfully');
    } catch (e) {
      console.error('💥 Switch error:', e);
      alert('Verkkovirhe organisaation vaihdossa.');
    }
  };

  // Clear session expired message
  const clearSessionExpiredMessage = useCallback(() => {
    setSessionExpiredMessage(null);
  }, []);

  // Refresh auth data (tenants, permissions, etc.)
  const refreshAuth = useCallback(async () => {
    await checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAdmin,
      isSuperAdmin,
      tenant,
      sessionExpiredMessage,
      clearSessionExpiredMessage,
      tenants,
      permissions,
      hasPermission,
      loading,
      csrfToken,
      needsConsent,
      onConsentComplete,
      login,
      register,
      logout,
      switchTenant,
      refreshAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};
